import { SITE_NAME } from "@/css/constants";
import { CheckoutQuery, PaymentMethodTypes } from "@/graphql";
import { PendingOrder } from "@/types/products";
import { ensureNonNullArray } from "@/utils/array";
import { parseDecimal } from "@/utils/string";

declare const affirm: ExplicitAny;

type User = NonNullable<CheckoutQuery["currentUser"]>;

interface HandleAffirmCheckoutProps {
  order: PendingOrder;
  user: User;
  affirmMode: string;
  handleFailedPayment: (error: string | null) => void;
  handleSuccessfulPayment: (
    paymentMethod: PaymentMethodTypes,
    checkoutToken: string,
  ) => void;
}

export function handleAffirmCheckout({
  order,
  user,
  affirmMode,
  handleFailedPayment,
  handleSuccessfulPayment,
}: HandleAffirmCheckoutProps) {
  const { firstName, lastName, email } = user;
  const totalInDollars = parseDecimal(
    order.total || order.creditTotalInDollars,
  );
  const orderItems = ensureNonNullArray(order?.orderItems);

  const productQtys = orderItems.reduce<Record<string, number>>((qtys, oi) => {
    qtys[oi.id] = (qtys[oi.id] || 0) + 1;
    return qtys;
  }, {});

  const items = orderItems.map((oi) => {
    const qty = productQtys[oi.id];
    const priceInDollars =
      parseDecimal(oi.price) || parseDecimal(oi.creditTotalInDollars) / qty;

    return {
      display_name: oi.product?.name || oi.product?.slug,
      sku: oi.id,
      unit_price: Math.round(priceInDollars * 100),
      qty: qty,
    };
  });

  affirm.checkout({
    merchant: {
      user_confirmation_url: `${API_HOST}/affirm_success`,
      user_cancel_url: window.location.href,
      user_confirmation_url_action: "POST",
      name: SITE_NAME,
    },
    shipping: {
      name: {
        first: firstName,
        last: lastName || "Mr/Mrs",
      },
      address: {
        line1: "440 N BARRANCA AVE",
        line2: "#1890",
        city: "COVINA",
        state: "CA",
        zipcode: "91723",
        country: "USA",
      },
      email: email,
    },
    items: items,
    order_id: order.id,
    currency: "USD",
    total: Math.round(totalInDollars * 100),
    metadata: {
      mode: affirmMode,
    },
  });
  affirm.checkout.open({
    onFail: function (error: ExplicitAny) {
      const errorText =
        (error && error.reason) || "Cannot checkout with affirm";
      handleFailedPayment(errorText !== "canceled" ? errorText : null);
    },
    onSuccess: function (checkout: ExplicitAny) {
      handleSuccessfulPayment(
        PaymentMethodTypes.Affirm,
        checkout.checkout_token,
      );
    },
  });
}
